import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { CpuChipIcon, UserIcon, PaperAirplaneIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { chatAPI, explorerAPI, userAPI } from '../../services/api';
import { CASES, kdpiColor } from './cases';

const modelApiUrl = process.env.REACT_APP_PYTHON_API_URL;

const deriveModel = (cart) => {
  if (!cart) return null;
  const probability = Number(cart.probability);
  const threshold = Number(cart.threshold);
  if (!Number.isFinite(probability) || !Number.isFinite(threshold)) return null;
  const interval = Array.isArray(cart.wilson_ci)
    ? cart.wilson_ci.map(Number)
    : null;
  return {
    prediction: probability >= threshold ? 'utilized' : 'not_utilized',
    probability,
    interval: interval?.length === 2 && interval.every(Number.isFinite) ? interval : null,
  };
};

const emptyAnswers = () => Object.fromEntries(CASES.map((c) => [c.id, { call: null, reason: '' }]));

const statusLabel = (answer) => {
  if (!answer?.call) return 'Not started';
  return answer.reason?.trim() ? 'Judgment + notes' : 'Judgment recorded';
};

const Outcome = ({ value }) => (
  <span className={value === 'utilized' ? 'text-emerald-700' : 'text-slate-700'}>
    {value === 'utilized' ? 'Likely accepted within 100 offers' : 'Likely not accepted within 100 offers'}
  </span>
);

export default function ClinicianWorklist() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const storageKey = `allociq-clinician-draft-${user.id ?? 'local'}`;
  const [storedBatch] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || 'null') || {}; }
    catch { return {}; }
  });
  const [answers, setAnswers] = useState(() => {
    return storedBatch.answers || emptyAnswers();
  });
  const [activeId, setActiveId] = useState(null);
  const [phase, setPhase] = useState(storedBatch.completed ? 'results' : 'worklist');
  const [processingIndex, setProcessingIndex] = useState(0);
  const [models, setModels] = useState(storedBatch.models || {});
  const [savedIds, setSavedIds] = useState(() => new Set(storedBatch.savedIds || []));
  const [sessionIds, setSessionIds] = useState(storedBatch.sessionIds || {});
  const [chatByCase, setChatByCase] = useState(storedBatch.chatByCase || {});
  const [chatInput, setChatInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [availableTokens, setAvailableTokens] = useState(0);
  const [minToken, setMinToken] = useState(10000);
  const [cameFromResults, setCameFromResults] = useState(false);
  const [saveError, setSaveError] = useState('');

  const activeCase = CASES.find((c) => c.id === activeId);
  const answeredCount = useMemo(
    () => CASES.filter((c) => answers[c.id]?.call).length,
    [answers]
  );
  const allAnswered = answeredCount === CASES.length;

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify({
      answers,
      models,
      sessionIds,
      chatByCase,
      savedIds: Array.from(savedIds),
      completed: phase === 'results',
    }));
  }, [answers, chatByCase, models, phase, savedIds, sessionIds, storageKey]);

  useEffect(() => {
    userAPI.getUserTokens().then((response) => {
      setAvailableTokens(response.data?.available_token || 0);
      setMinToken(response.data?.min_token || 10000);
    }).catch(() => setAvailableTokens(0));
  }, []);

  const setAnswer = (caseId, patch) => {
    setAnswers((prev) => ({ ...prev, [caseId]: { ...prev[caseId], ...patch } }));
  };

  const startReview = (caseId) => {
    setCameFromResults(false);
    setChatInput('');
    setActiveId(caseId);
    setPhase('case');
  };

  const navigateCase = (direction) => {
    const currentIndex = CASES.findIndex((c) => c.id === activeId);
    const next = CASES[Math.max(0, Math.min(CASES.length - 1, currentIndex + direction))];
    if (next) {
      setChatInput('');
      setActiveId(next.id);
    }
  };

  const runModel = async (caseInfo, { force = false } = {}) => {
    const sessionId = sessionIds[caseInfo.id] || uuidv4();
    setSessionIds((prev) => ({ ...prev, [caseInfo.id]: sessionId }));
    localStorage.setItem('session_id', sessionId);
    if (models[caseInfo.id] && !force) return { model: models[caseInfo.id], sessionId };
    if (!modelApiUrl) return { model: null, sessionId };
    const csv = await fetch(`/samples/experimental_cases/${caseInfo.file}`);
    if (!csv.ok) throw new Error(`Could not load ${caseInfo.file}`);
    const file = new File([await csv.text()], caseInfo.file, { type: 'text/csv' });
    const form = new FormData();
    form.append('file', file);
    form.append('user_id', String(user.id ?? ''));
    form.append('session_id', sessionId);
    form.append('selected_index', 0);
    const response = await axios.post(`${modelApiUrl}/utilization/run`, form);
    if (response.data?.status !== 'success') throw new Error(response.data?.message || 'Model run failed');
    const model = deriveModel(response.data.data?.cart_confidence);
    if (model) setModels((prev) => ({ ...prev, [caseInfo.id]: model }));
    return { model, sessionId };
  };

  const addChatMessage = (caseId, message) => {
    setChatByCase((prev) => ({ ...prev, [caseId]: [...(prev[caseId] || []), message] }));
  };

  const sendChatMessage = async (rawText) => {
    const text = rawText.trim();
    if (!text || typing || availableTokens < minToken || !activeCase) return;
    const caseId = activeCase.id;
    addChatMessage(caseId, { role: 'user', text });
    setChatInput('');
    setTyping(true);
    try {
      const { model, sessionId } = await runModel(activeCase, { force: true });
      if (!model) throw new Error('The model could not be loaded for this case, so the assistant is unavailable.');
      localStorage.setItem('session_id', sessionId);
      const response = await chatAPI.sendMessage({ message: text, selectedModule: 'utilization' });
      const answer = response.data?.success
        ? (response.data.response?.answer || response.data.response || 'No response.')
        : (response.data?.message || 'The assistant could not answer.');
      addChatMessage(caseId, { role: 'bot', text: answer });
      userAPI.getUserTokens().then((res) => setAvailableTokens(res.data?.available_token || 0)).catch(() => {});
    } catch (error) {
      addChatMessage(caseId, { role: 'bot', text: error.response?.data?.message || error.message || 'Network error. Please try again.' });
    } finally {
      setTyping(false);
    }
  };

  const submitBatch = async () => {
    if (!allAnswered) return;
    setSaveError('');
    setPhase('processing');
    const collectedModels = { ...models };
    const collectedSaved = new Set(savedIds);
    let firstFailure = '';

    for (let i = 0; i < CASES.length; i += 1) {
      const caseInfo = CASES[i];
      setProcessingIndex(i);
      let model = collectedModels[caseInfo.id] || null;
      if (!model) {
        try {
          const run = await runModel(caseInfo);
          model = run?.model || null;
          if (model) collectedModels[caseInfo.id] = model;
        } catch (error) {
          console.warn(`Model unavailable for case ${caseInfo.id}:`, error);
          // The independent clinician judgment is still valuable if the model is down.
        }
      }

      if (collectedSaved.has(caseInfo.id)) continue;
      try {
        await explorerAPI.saveResponse({
          caseId: caseInfo.id,
          caseFile: caseInfo.file,
          userCall: answers[caseInfo.id].call,
          reason: answers[caseInfo.id].reason,
          modelPrediction: model?.prediction ?? null,
          modelProbability: model?.probability ?? null,
          trueOutcome: caseInfo.trueOutcome,
        });
        collectedSaved.add(caseInfo.id);
        setSavedIds(new Set(collectedSaved));
      } catch (error) {
        console.error(`Could not save case ${caseInfo.id}:`, error);
        if (!firstFailure) firstFailure = `Could not save Case ${caseInfo.id}. Your judgments remain saved in this browser; retry to finish submitting the batch.`;
      }
      setModels({ ...collectedModels });
    }

    setModels(collectedModels);
    if (collectedSaved.size === CASES.length) {
      setPhase('results');
    } else {
      setSaveError(firstFailure || 'Some responses were not saved. Retry to finish submitting the batch.');
      setPhase('submit-error');
    }
  };

  if (phase === 'worklist' || phase === 'submit-error') {
    return (
      <main className="min-h-[calc(100vh-4rem)] bg-gray-50 px-4 py-8 sm:px-6">
        <div className="mx-auto max-w-5xl">
          <header className="mb-6">
            <p className="text-sm font-semibold uppercase tracking-wide text-primary-700">Clinician pilot</p>
            <h1 className="mt-1 text-3xl font-bold text-gray-900">Your case worklist</h1>
            <p className="mt-2 max-w-3xl text-sm text-gray-600">
              The results panel stays hidden until the batch is submitted. The assistant is available throughout and may influence judgments on later cases.
            </p>
          </header>

          <section className="mb-5 rounded-xl border border-primary-100 bg-white p-5 shadow-sm" aria-label="Batch progress">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-gray-900">Batch progress</p>
                <p className="mt-1 text-sm text-gray-600">{answeredCount} of {CASES.length} independent judgments recorded</p>
              </div>
              <div className="text-sm font-semibold text-primary-800">{Math.round((answeredCount / CASES.length) * 100)}%</div>
            </div>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-100">
              <div className="h-full rounded-full bg-primary-600 transition-all" style={{ width: `${(answeredCount / CASES.length) * 100}%` }} />
            </div>
          </section>

          {saveError && (
            <div role="alert" className="mb-5 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
              {saveError}
            </div>
          )}

          <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="hidden grid-cols-[minmax(0,1fr)_minmax(100px,140px)_150px_150px] gap-4 border-b border-gray-200 bg-gray-50 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 sm:grid">
              <span>Case</span><span>KDPI</span><span>Decision</span><span>Status</span>
            </div>
            <ul className="divide-y divide-gray-100">
              {CASES.map((caseInfo) => {
                const answer = answers[caseInfo.id];
                return (
                  <li key={caseInfo.id} className="grid gap-3 px-5 py-4 sm:grid-cols-[minmax(0,1fr)_minmax(100px,140px)_150px_150px] sm:items-center sm:gap-4">
                    <div>
                      <p className="font-semibold text-gray-900">Case {caseInfo.id}</p>
                      <p className="mt-1 text-sm text-gray-600">{caseInfo.donorAge} yrs · Blood type {caseInfo.blood} · KDPI {caseInfo.kdpi}%</p>
                    </div>
                    <div className="flex items-center" role="img" aria-label={`KDPI ${caseInfo.kdpi}%`}>
                      <div className="h-2 w-full max-w-36 overflow-hidden rounded-full bg-gray-100">
                        <div className="h-full min-w-[2px] rounded-full" style={{ width: `${caseInfo.kdpi}%`, backgroundColor: kdpiColor(caseInfo.kdpi) }} />
                      </div>
                    </div>
                    <div className="text-sm text-gray-700">
                      {answer?.call ? <Outcome value={answer.call} /> : <span className="text-gray-400">Not recorded</span>}
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span className={`text-sm ${answer?.call ? 'font-medium text-emerald-700' : 'text-gray-500'}`}>{statusLabel(answer)}</span>
                      <button onClick={() => startReview(caseInfo.id)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-800 hover:border-primary-400 hover:bg-primary-50">
                        {allAnswered ? 'View' : answer?.call ? 'Edit' : 'Review'}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>

          <div className="mt-5 flex flex-col items-start justify-between gap-3 rounded-xl border border-gray-200 bg-white p-5 sm:flex-row sm:items-center">
            <p className="text-sm text-gray-600">You can return to the worklist at any time. Draft judgments are saved in this browser.</p>
            <button onClick={submitBatch} disabled={!allAnswered} className="w-full rounded-lg bg-primary-700 px-5 py-3 text-sm font-semibold text-white hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto">
              {phase === 'submit-error' ? 'Retry batch submission' : 'Submit all independent judgments'}
            </button>
          </div>
        </div>
      </main>
    );
  }

  if (phase === 'case' && activeCase) {
    const answer = answers[activeCase.id] || { call: null, reason: '' };
    const index = CASES.findIndex((c) => c.id === activeCase.id);
    const chatMessages = chatByCase[activeCase.id] || [];
    const chatSuggestions = [
      'Why did the model predict this?',
      'What does the KDPI mean for this kidney?',
      'Which factors mattered most?',
    ];
    return (
      <main className="flex min-h-[calc(100vh-4rem)] flex-col bg-gray-50 lg:flex-row">
        <aside className="flex w-full flex-shrink-0 flex-col border-b border-gray-200 bg-white lg:w-72 lg:border-b-0 lg:border-r">
          <div className="border-b border-gray-200 px-4 py-4">
            <h2 className="flex items-center text-base font-semibold text-gray-800"><span className="mr-2 text-primary-600">♧</span>Donor kidney cases</h2>
            <p className="mt-1 text-sm text-gray-500">{answeredCount} of {CASES.length} judgments recorded</p>
          </div>
          <div className="flex gap-2 overflow-x-auto p-2 lg:flex-1 lg:flex-col lg:overflow-y-auto">
            {CASES.map((caseInfo, caseIndex) => {
              const caseAnswer = answers[caseInfo.id];
              return (
                <button key={caseInfo.id} onClick={() => { setChatInput(''); setActiveId(caseInfo.id); }}
                  className={`min-w-40 rounded-lg border px-3 py-2 text-left transition-colors lg:w-full ${caseInfo.id === activeCase.id ? 'border-primary-300 bg-primary-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}>
                  <span className="flex items-center justify-between gap-2"><span className="text-sm font-medium text-gray-800">Case {caseInfo.id}</span><span className={`text-[10px] ${caseAnswer?.call ? 'font-semibold text-emerald-700' : 'text-gray-400'}`}>{caseAnswer?.call ? 'Judgment saved' : 'Not reviewed'}</span></span>
                  <span className="mt-1 flex items-center justify-between gap-2"><span className="text-xs text-gray-500">{caseInfo.donorAge} y/o · {caseInfo.blood} · KDPI {caseInfo.kdpi}%</span><span className="h-2 w-2 rounded-full" style={{ backgroundColor: kdpiColor(caseInfo.kdpi) }} /></span>
                  <span className="sr-only">Case {caseIndex + 1} of {CASES.length}</span>
                </button>
              );
            })}
          </div>
          <div className="hidden border-t border-gray-200 px-4 py-3 text-xs text-gray-400 lg:block">Experimental preview · sample data is anonymized.</div>
        </aside>

        <section className="min-w-0 flex-1 overflow-y-auto">
          <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
            <div className="mb-5 flex flex-wrap items-center gap-2">
              <button onClick={() => setPhase(cameFromResults ? 'results' : 'worklist')} className="mr-1 text-sm text-primary-700 hover:text-primary-900">← {cameFromResults ? 'Results' : 'Worklist'}</button>
              <h1 className="text-xl font-bold text-gray-900">100-Offer Acceptance Explorer</h1>
              <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-amber-800">Experimental</span>
            </div>
            <article className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <header className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-6 py-5">
                <h2 className="text-lg font-semibold text-gray-900">Case {activeCase.id} · Donor kidney offer</h2>
                <span className={`rounded-full px-3 py-1 text-sm font-medium ${activeCase.ecd ? 'bg-amber-100 text-amber-800' : 'bg-primary-50 text-primary-700'}`}>{activeCase.ecd ? 'Expanded-criteria' : 'Standard-criteria'}</span>
              </header>
              <div className="space-y-6 p-6">
                <section aria-label="Kidney donor profile index">
                  <div className="mb-2 flex items-center justify-between"><h3 className="font-semibold text-gray-900">KDPI — Kidney Donor Profile Index</h3><span className="font-semibold" style={{ color: kdpiColor(activeCase.kdpi) }}>{activeCase.kdpi}%</span></div>
                  <div className="h-3 overflow-hidden rounded-full bg-gray-100"><div className="h-full transition-all" style={{ width: `${activeCase.kdpi}%`, backgroundColor: kdpiColor(activeCase.kdpi) }} /></div>
                  <p className="mt-2 text-sm text-gray-500">Lower is higher quality (0% = best expected longevity, 100% = highest risk).</p>
                </section>

                <section className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {[
                    ['Donor age', `${activeCase.donorAge} yrs`], ['Blood type', activeCase.blood], ['Cause of death', activeCase.cod],
                    ['Hypertension', activeCase.hypertension ? 'Yes' : 'No'], ['Diabetes', activeCase.diabetes ? 'Yes' : 'No'], ['Smoking history', activeCase.smoker ? 'Yes' : 'No'],
                    ['Serum creatinine', `${activeCase.creatinine} mg/dL`], ['BMI', activeCase.bmi],
                  ].map(([label, value]) => (
                    <div key={label} className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3"><p className="text-xs uppercase tracking-wide text-gray-500">{label}</p><p className="mt-1 text-sm font-semibold text-gray-900">{value}</p></div>
                  ))}
                </section>

                <fieldset>
                  <legend className="mb-3 text-sm font-semibold text-gray-900">Your call: will this kidney be accepted within 100 offers?</legend>
                  <p className="mb-3 text-xs text-gray-500">{allAnswered ? 'Your independent judgment is locked for this batch.' : 'For an independent assessment, ideally record every judgment before prompting the assistant; its answer may influence later cases.'}</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {[
                      ['utilized', 'Likely accepted within 100 offers'], ['not_utilized', 'Likely not accepted within 100 offers'],
                    ].map(([value, label]) => (
                      <button key={value} type="button" aria-pressed={answer.call === value} disabled={allAnswered} onClick={() => setAnswer(activeCase.id, { call: value })}
                        className={`flex items-center justify-center gap-2 rounded-lg border-2 px-4 py-3 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-70 ${answer.call === value ? 'border-emerald-300 bg-emerald-50 text-emerald-900' : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-400'}`}>
                        <span className={`h-2.5 w-2.5 rounded-full ${value === 'utilized' ? 'bg-emerald-500' : 'bg-slate-400'}`} />{label}
                      </button>
                    ))}
                  </div>
                </fieldset>

                <div>
                  <label htmlFor="clinical-reason" className="mb-2 block text-sm font-semibold text-gray-900">Reasoning <span className="font-normal text-gray-500">(optional)</span></label>
                  <textarea id="clinical-reason" rows={3} value={answer.reason || ''} disabled={allAnswered} onChange={(e) => setAnswer(activeCase.id, { reason: e.target.value })}
                    placeholder="Note the factors that informed your call…" className="w-full resize-y rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 disabled:bg-gray-50" />
                  <p className="mt-1 text-xs text-gray-500">Draft saved automatically in this browser.</p>
                </div>
              </div>
              <footer className="flex items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4">
                <button onClick={() => navigateCase(-1)} disabled={index === 0} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-white disabled:cursor-not-allowed disabled:opacity-40">Previous</button>
                <button onClick={() => { if (index < CASES.length - 1) navigateCase(1); else setPhase(cameFromResults ? 'results' : 'worklist'); }} disabled={!answer.call} className="rounded-lg bg-primary-700 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-40">Save and continue to next case</button>
              </footer>
            </article>

            <section className="mt-6 border-t border-gray-200 pt-4" aria-label="Case assistant">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="flex items-center text-sm font-medium text-gray-700"><CpuChipIcon className="mr-2 h-4 w-4 text-primary-500" />Assistant</h2>
                <button onClick={() => setChatByCase((prev) => ({ ...prev, [activeCase.id]: [] }))} disabled={chatMessages.length === 0} className="text-xs text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed">Clear chat</button>
              </div>
              <>
                  <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                    <ExclamationTriangleIcon className="mr-2 inline h-4 w-4" />For the most independent assessment, record all case judgments before prompting the model. Assistant responses may influence judgments on later cases.
                  </div>
                  <div className="max-h-80 min-h-20 space-y-3 overflow-y-auto rounded-lg">
                    {chatMessages.length === 0 && <p className="py-5 text-center text-sm text-gray-400">Ask the assistant about this donor kidney — it runs the real 100-offer acceptance model for this case.</p>}
                    {chatMessages.map((message, messageIndex) => (
                      <div key={`${activeCase.id}-${messageIndex}`} className={`flex items-start gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}>
                        <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${message.role === 'user' ? 'bg-blue-600' : 'bg-gray-600'}`}>{message.role === 'user' ? <UserIcon className="h-4 w-4 text-white" /> : <CpuChipIcon className="h-4 w-4 text-white" />}</div>
                        <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm leading-relaxed ${message.role === 'user' ? 'rounded-tr-sm bg-blue-600 text-white' : 'rounded-tl-sm bg-gray-100 text-gray-800'}`}>{message.text}</div>
                      </div>
                    ))}
                    {typing && <p className="pl-11 text-sm text-gray-400">Assistant is responding…</p>}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {chatSuggestions.map((suggestion) => <button key={suggestion} onClick={() => sendChatMessage(suggestion)} disabled={typing || availableTokens < minToken} className="rounded-full border border-gray-300 px-3 py-1.5 text-xs text-gray-600 hover:border-primary-400 hover:bg-white disabled:opacity-50">{suggestion}</button>)}
                  </div>
                  {availableTokens < minToken && <p className="mt-3 flex items-center text-xs text-amber-700"><ExclamationTriangleIcon className="mr-1 h-4 w-4" />Chat requires {minToken.toLocaleString()} tokens; balance {availableTokens.toLocaleString()}.</p>}
                  <form onSubmit={(event) => { event.preventDefault(); sendChatMessage(chatInput); }} className="mt-3 flex items-center gap-3">
                    <input value={chatInput} onChange={(event) => setChatInput(event.target.value)} placeholder="Ask about this kidney…" disabled={typing || availableTokens < minToken} className="min-w-0 flex-1 rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 disabled:bg-gray-100" />
                    <button type="submit" disabled={!chatInput.trim() || typing || availableTokens < minToken} className="flex items-center gap-2 rounded-xl bg-primary-700 px-4 py-3 text-white hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-50"><PaperAirplaneIcon className="h-4 w-4" /><span className="text-sm font-medium">Send</span></button>
                  </form>
                  <p className="mt-2 text-center text-xs text-gray-400">Assistant answers are case-specific. Chat requires sufficient tokens and an available model service.</p>
              </>
            </section>
          </div>
        </section>
      </main>
    );
  }

  if (phase === 'processing') {
    return <main className="flex min-h-[calc(100vh-4rem)] items-center justify-center bg-gray-50 px-6"><div className="w-full max-w-lg rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm"><div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-primary-100 border-t-primary-700" /><h1 className="mt-5 text-xl font-bold text-gray-900">Submitting your independent judgments</h1><p className="mt-2 text-sm text-gray-600">Saving case {Math.min(processingIndex + 1, CASES.length)} of {CASES.length}. Model results and outcomes remain hidden until the batch is complete.</p><div className="mt-5 h-2 overflow-hidden rounded-full bg-gray-100"><div className="h-full bg-primary-600 transition-all" style={{ width: `${((processingIndex + 1) / CASES.length) * 100}%` }} /></div></div></main>;
  }

  return (
    <main className="min-h-[calc(100vh-4rem)] bg-gray-50 px-4 py-8 sm:px-6">
      <div className="mx-auto max-w-4xl">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3"><div><p className="text-sm font-semibold uppercase tracking-wide text-primary-700">Batch complete</p><h1 className="mt-1 text-3xl font-bold text-gray-900">Review results</h1></div><span className="rounded-full bg-emerald-100 px-3 py-1 text-sm font-semibold text-emerald-800">{CASES.length} of {CASES.length} saved</span></div>
        <p className="mb-5 text-sm text-gray-600">Your independent judgments were saved before model predictions and outcomes were revealed.</p>
        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <ul className="divide-y divide-gray-100">
            {CASES.map((caseInfo) => {
              const model = models[caseInfo.id];
              const physicianCall = answers[caseInfo.id]?.call;
              return <li key={caseInfo.id} className="grid gap-3 px-5 py-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto] md:items-center"><div><p className="font-semibold text-gray-900">Case {caseInfo.id}</p><p className="mt-1 text-sm">Your judgment: <Outcome value={physicianCall} /></p></div><div><p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Model</p><p className="mt-1 text-sm text-gray-800">{model ? <><Outcome value={model.prediction} /> · {(model.probability * 100).toFixed(1)}%{model.interval ? ` · interval ${(model.interval[0] * 100).toFixed(1)}–${(model.interval[1] * 100).toFixed(1)}%` : ''}</> : 'Unavailable for this case'}</p></div><div><p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Actual outcome</p><p className="mt-1 text-sm"><Outcome value={caseInfo.trueOutcome} /></p></div><button onClick={() => { setCameFromResults(true); setActiveId(caseInfo.id); setPhase('case'); }} className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50">Review case</button></li>;
            })}
          </ul>
        </section>
        <div className="mt-5 flex justify-end"><button onClick={() => { localStorage.removeItem(storageKey); setAnswers(emptyAnswers()); setModels({}); setSessionIds({}); setChatByCase({}); setSavedIds(new Set()); setCameFromResults(false); setPhase('worklist'); }} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Start a new batch</button></div>
      </div>
    </main>
  );
}
